import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { COMMENT_LIMIT, cleanComment } from "./reviewUtils";

/**
 * The comment of a review step: a decision, a resubmit or a vote. `hint` says why it is needed (shown while
 * `required` and empty), `error` what the server refused (a comment too long); both are read with the field.
 */
const CommentField = forwardRef(function CommentField(
  { id, label, placeholder, value, onChange, required = false, hint = "", error = "", disabled = false },
  ref
) {
  const { t } = useTranslation();
  const missing = required && !cleanComment(value);
  const describedBy = [error && `${id}-error`, missing && hint && `${id}-hint`].filter(Boolean).join(" ") || undefined;
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
      </label>
      <textarea
        ref={ref}
        id={id}
        rows={4}
        className={`block w-full resize-y rounded-lg px-3 py-2 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:ring-4 disabled:cursor-not-allowed disabled:bg-slate-50 ${
          error
            ? "border-red-400 focus:border-red-600 focus:ring-red-600/15"
            : "border-slate-300 focus:border-blue-600 focus:ring-blue-600/15"
        }`}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={COMMENT_LIMIT}
        disabled={disabled}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      <div className="mt-1 flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          {error && (
            <p id={`${id}-error`} className="text-xs font-medium text-red-600">
              {error}
            </p>
          )}
          {!error && missing && hint && (
            <p id={`${id}-hint`} className="text-xs font-medium text-amber-700">
              {hint}
            </p>
          )}
        </div>
        <p
          id={`${id}-count`}
          className={`ml-auto shrink-0 text-xs tabular-nums ${value.length >= COMMENT_LIMIT ? "text-red-600" : "text-slate-500"}`}
        >
          {value.length}
          {t("question_detail.comment.of_max_characters", { max: COMMENT_LIMIT })}
        </p>
      </div>
    </div>
  );
});

export default CommentField;
